import { Provider, ProviderName, providers } from '@indigohive/cogfy-providers'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import { useCogfy, useTitle, useToasts, useWorkspace } from '../../hooks'
import { ConfirmDeleteDialog, Container, SettingsPagesHeader } from '../../components'
import { ConnectionSection } from './components'
import { WandSparkles } from 'lucide-react'
import { CreateConnectionDialog } from './components/CreateConnectionDialog'
import { GetConnectionsListResultData } from '@indigohive/cogfy-types/endpoints/getConnectionsList'
import { UUID } from '@indigohive/cogfy-types'
import { SearchInput } from '../CollectionPageV2/components/SearchInput'

export const USE_CUSTOM_DIALOG = new Set<ProviderName>([
  'aws',
  'azureAd',
  'github',
  'googleCalendar',
  'googleDrive',
  'govbr',
  'heroku',
  'jira',
  'http',
  'metaMarketingApi',
  'postgresql',
  'rdStation',
  'whatsapp'
])

export function WorkspaceConnectionsPage () {
  const params = useParams<{ workspace: string }>()
  const cogfy = useCogfy()
  const getWorkspace = useWorkspace(params.workspace!)
  const { t } = useTranslation()
  const location = useLocation()
  const toasts = useToasts()
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
  const [selectedConnection, setSelectedConnection] = useState<UUID | null>(null)
  const [providerToAdd, setProviderToAdd] = useState<Provider | null>(null)
  const [searchConnectionName, setSearchConnectionName] = useState('')
  const filteredProviders = providers.filter(provider =>
    provider.name.toLowerCase().includes(searchConnectionName.toLowerCase()) &&
    provider.name.toLowerCase() !== 'http'
  )

  const deleteConnection = useMutation({
    mutationFn: async () => {
      if (selectedConnection) {
        await cogfy.deleteConnection({ connectionId: selectedConnection })
      }
    },
    onSuccess: () => {
      toasts.info(t('WorkspaceConnectionsPage:Delete connection successfully'))
      getConnections.refetch()
      setOpenDeleteDialog(false)
    },
    onError: () => {
      toasts.error(t('WorkspaceConnectionsPage:Error to delete connection'))
    }
  })

  const getConnections = useQuery({
    queryKey: ['getConnections'],
    queryFn: ({ signal }) => cogfy.getConnectionsList({}, { signal })
  })

  const connectionsByProvider = getConnections.data?.data
    .reduce<Partial<Record<string, GetConnectionsListResultData[]>>>(
    (acc, connection) => {
      if (!acc[connection.provider]) {
        acc[connection.provider] = []
      }

      acc[connection.provider]?.push(connection)

      return acc
    }, {}) ?? {}

  useTitle({
    title: t('Connections'),
    loading: getWorkspace.isLoading,
    error: getWorkspace.isError
  })

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash)

      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [location.hash])

  return (
    <div className="max-h-screen">
      <SettingsPagesHeader
        title={t('Connections')}
        description={t('WorkspaceConnectionsPage:Configure and customize connections with external services')}
      >
        <WandSparkles size={46} />
      </SettingsPagesHeader>
      <Container className="py-4">
        <CreateConnectionDialog
          provider={providerToAdd}
          open={Boolean(providerToAdd)}
          onClose={() => setProviderToAdd(null)}
          onSuccess={() => {
            setProviderToAdd(null)
            getConnections.refetch()
          }}
        />
        <div className="max-w-screen-sm flex flex-col items-center space-y-4 my-12 mx-auto">
          <SearchInput
            searchName={searchConnectionName}
            onNameChange={setSearchConnectionName}
            className='w-full h-11'
          />
          {!getConnections.data && !getConnections.isLoading && (
            <span className='self-start pl-16'>{t('Error when searching connections')}</span>
          )}
          {getConnections.isLoading && <progress className="progress w-1/3" />}
          {getConnections.data && filteredProviders.length === 0 && (
            <span className=''>{t('WorkspaceConnectionsPage:No providers found')}</span>
          )}
          {getConnections.data &&
            filteredProviders.map(provider => (
              <ConnectionSection
                key={provider.name}
                onConnectClick={USE_CUSTOM_DIALOG.has(provider.name)
                  ? undefined
                  : provider => { setProviderToAdd(provider) }
                }
                onDeleteClick={(id) => {
                  setSelectedConnection(id)
                  setOpenDeleteDialog(true)
                }}
                disableDelete={deleteConnection.isPending}
                provider={provider}
                connections={connectionsByProvider[provider.name] ?? []}
                onRefetch={() => { getConnections.refetch() }}
              />
            ))
          }
        </div>
      </Container>
      <ConfirmDeleteDialog
        title={t('WorkspaceConnectionsPage:Delete selected connection?')}
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={deleteConnection.mutate}
      />
    </div>
  )
}
